<template>
  <div class="api-line-selector">
    <van-nav-bar title="切换线路" left-arrow @click-left="$router.back()" />
    <div class="legend">
      <span><i class="dot fast"></i>高</span>
      <span><i class="dot medium"></i>中</span>
      <span><i class="dot slow"></i>低</span>
    </div>
    <van-cell-group>
      <van-radio-group v-model="selectedUrl" @change="onSelect">
        <van-cell
          v-for="(node, idx) in nodes"
          :key="idx"
          :clickable="node.latency != null"
          :title="lineTitle(idx, node)"
          @click="node.latency != null && (selectedUrl = node.url)"
        >
          <template slot="label">
            <!-- <span class="node-port">Node Port {{ portOf(node.url) }}</span> -->
          </template>
          <template slot="right-icon">
            <div class="latency" :class="speedClass(node.latency)">
              <span v-if="node.testing">...</span>
              <span v-else>{{ latencyText(node.latency) }}</span>
            </div>
            <van-radio :name="node.url" :disabled="node.latency == null" />
          </template>
        </van-cell>
      </van-radio-group>
    </van-cell-group>
    <div class="footer" v-if="!isTesting && !hasWorkingNode">
      <van-button type="primary" block @click="openAddDialog">添加线路</van-button>
    </div>
    <van-dialog
      v-model="showAddDialog"
      title="添加线路"
      show-cancel-button
      @confirm="confirmAddNode"
    >
      <div class="dialog-body">
        <van-field
          v-model="newNodeUrl"
          label="URL"
          label-width="auto"
          placeholder="https://example.com/"
          clearable
        />
      </div>
    </van-dialog>
  </div>
</template>

<script>
export default {
  name: "ApiLineSelector",
  data() {
    return {
      nodes: [],
      selectedUrl: localStorage.getItem("lineUrl"),
      showAddDialog: false,
      newNodeUrl: "",
    };
  },
  computed: {
    hasWorkingNode() {
      return this.nodes.some((n) => n.latency != null);
    },
    isTesting() {
      return this.nodes.some((n) => n.testing);
    },
  },
  created() {
    this.initNodes();
    this.fetchRemoteApiLines();
  },
  methods: {
    initNodes() {
      // Build nodes using unified model: { id, name, url, temp } plus optional latency/testing
      let nodes = [];
      try {
        const stored = JSON.parse(localStorage.getItem("api_lines") || "[]");
        if (Array.isArray(stored)) {
          if (stored.length && typeof stored[0] === "object") {
            nodes = stored
              .filter((i) => i && i.url)
              .map((i) => ({
                id: i.id != null ? i.id : 0,
                name: i.name || "线路",
                url: i.url,
                temp: !!i.temp,
                latency: i.latency != null ? i.latency : null,
                testing: !!i.testing,
              }));
          } else {
            // Legacy [string] → normalize
            nodes = stored.filter(Boolean).map((u, idx) => ({
              id: idx + 1,
              name: `线路 ${idx + 1}`,
              url: u,
              temp: false,
              latency: null,
              testing: true,
            }));
          }
        }
      } catch (e) {}
      // Merge defaults from $URLs (ensure presence, no duplicates)
      if (this.$URLs && Array.isArray(this.$URLs)) {
        this.$URLs.forEach((item) => {
          if (!item || !item.url) return;
          if (!nodes.some((n) => n.url === item.url)) {
            nodes.push({
              id: item.id != null ? item.id : 0,
              name: item.name || `线路 ${nodes.length + 1}`,
              url: item.url,
              temp: !!item.temp,
              latency: null,
              testing: true,
            });
          }
        });
      }
      // Fallback if still empty: use env values
      if (!nodes.length && window.__env) {
        const fallbacks = [
          window.__env.API_ROOT,
          window.__env.Line_1,
          window.__env.Line_2,
        ].filter(Boolean);
        nodes = fallbacks.map((u, idx) => ({
          id: idx + 1,
          name: `线路 ${idx + 1}`,
          url: u,
          temp: false,
          latency: null,
          testing: true,
        }));
      }
      // Ensure unique by url and assign stable ids if missing/duplicate
      const seen = new Set();
      nodes = nodes.filter((n) => {
        if (seen.has(n.url)) return false;
        seen.add(n.url);
        return true;
      });
      // Reindex ids sequentially to maintain consistency
      nodes = nodes.map((n, idx) => ({
        ...n,
        id: idx + 1,
        name:
          n.name && n.name.startsWith("线路")
            ? `线路 ${idx + 1}`
            : n.name || `线路 ${idx + 1}`,
      }));
      this.nodes = nodes;
      // Persist normalized model
      try {
        localStorage.setItem("api_lines", JSON.stringify(this.nodes));
      } catch (e) {}
      // Begin latency checks
      //   this.$nextTick(() => this.measureAll());
    },
    async fetchRemoteApiLines() {
      // Simple fetch with CORS; just log the response
      try {
        const res = await fetch(window.__env.line_api + "/config/getapilist", {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body:
            "name=" +
            window.__env.line_api_name +
            "&token=" +
            window.__env.line_api_token,
        });
        const text = await res.text();
        try {
          const json = JSON.parse(text);
          console.log("[ApiLineSelector] getapilist json:", json);
          let data = json.data;
          if (data && data.length > 0) {
            // Determine current max id to continue numbering
            let maxId =
              this.nodes.reduce((m, i) => Math.max(m, i && i.id ? i.id : 0), 0) || 0;
            data.forEach((item) => {
              if (!item || !item.url) return;
              if (this.nodes.some((node) => node.url === item.url)) return;
              maxId += 1;
              const normalized = {
                id: maxId,
                name: item.name || `线路 ${maxId}`,
                url: item.url,
                temp: false,
                latency: null,
                testing: true,
              };
              this.nodes.push(normalized);
            });
          }
        } catch (err) {
          // non-JSON response; already logged as text
        }
      } catch (e) {
        console.log("[ApiLineSelector] getapilist error:", e);
      } finally {
        this.$nextTick(() => {
          // Persist using the unified model while keeping latency/testing
          try {
            localStorage.setItem("api_lines", JSON.stringify(this.nodes));
          } catch (e) {}
          this.measureAll();
        });
      }
    },
    measureAll() {
      this.nodes.forEach((node) => this.measureLatency(node));
    },
    async measureLatency(node) {
      node.testing = true;
      node.latency = await this.ping(node.url);
      node.testing = false;
      // keep unreachable nodes visible but not selectable; just re-sort list
      //   this.sortNodes();
    },
    ping(url) {
      return new Promise((resolve) => {
        const start = Date.now();
        const controller = "AbortController" in window ? new AbortController() : null;
        const timeoutId = setTimeout(() => {
          if (controller) controller.abort();
          resolve(null);
        }, 5000);
        fetch(url, {
          mode: "no-cors",
          cache: "no-store",
          signal: controller ? controller.signal : undefined,
        })
          .then(() => {
            clearTimeout(timeoutId);
            resolve(Date.now() - start);
          })
          .catch(() => {
            clearTimeout(timeoutId);
            resolve(null);
          });
      });
    },
    onSelect(val) {
      // val is the radio name, which we bind to node.url (string)
      const url = val;
      localStorage.setItem("lineUrl", url);
      if (this.$http) {
        this.$http.defaults.baseURL = url;
      }
      this.$toast.success({
        message: "切换线路成功",
        onClose: () => {
          this.$router.back();
        },
      });
      // Ensure toast-nowrap is in <style> somewhere in the component or global CSS:
      // .toast-nowrap { white-space: nowrap; }
    },
    portOf(rawUrl) {
      try {
        const a = document.createElement("a");
        a.href = rawUrl;
        if (a.port) return a.port;
        return a.protocol === "https:" ? 443 : 80;
      } catch (e) {
        return "";
      }
    },
    latencyText(ms) {
      return ms == null ? "—" : `${ms}ms`;
    },
    speedClass(ms) {
      if (ms == null) return "slow";
      if (ms <= 200) return "fast";
      if (ms <= 1000) return "medium";
      return "slow";
    },
    rescan() {
      this.measureAll();
    },
    lineTitle(idx, node) {
      return `线路 ${idx + 1}`;
    },
    sortNodes() {
      // Tested nodes first, ordered by ascending latency; testing nodes at the end
      this.nodes = this.nodes.slice().sort((a, b) => {
        if (a.testing && b.testing) return 0;
        if (a.testing) return 1;
        if (b.testing) return -1;
        return (a.latency || Infinity) - (b.latency || Infinity);
      });
    },
    openAddDialog() {
      this.newNodeUrl = "";
      this.showAddDialog = true;
    },
    confirmAddNode() {
      const normalized = this.normalizeUrl(this.newNodeUrl);
      if (!normalized) {
        this.$toast.fail("请联系客服获取正确的线路");
        return;
      }
      if (this.nodes.find((n) => n.url === normalized)) {
        this.$toast("线路已存在");
        return;
      }
      const node = {
        id: this.nodes.length + 1,
        name: `线路 ${this.nodes.length + 1}`,
        url: normalized,
        temp: true,
        latency: null,
        testing: true,
      };
      this.nodes.push(node);
      // Persist new node into localStorage api_lines (object form)
      try {
        let existing = JSON.parse(localStorage.getItem("api_lines") || "[]");
        if (!Array.isArray(existing)) existing = [];
        // Normalize to object array
        if (existing.length && typeof existing[0] !== "object") {
          existing = existing.filter(Boolean).map((u, idx) => ({
            id: idx + 1,
            name: `线路 ${idx + 1}`,
            url: u,
            temp: false,
            latency: null,
            testing: true,
          }));
        }
        if (!existing.find((i) => i && i.url === normalized)) {
          const maxId = existing.reduce((m, i) => Math.max(m, i && i.id ? i.id : 0), 0);
          const newItem = {
            id: maxId + 1,
            name: `线路 ${maxId + 1}`,
            url: normalized,
            temp: true,
            latency: null,
            testing: true,
          };
          existing.push(newItem);
          localStorage.setItem("api_lines", JSON.stringify(existing));

          this.$toast.success("添加线路成功");

        }
      } catch (e) {}
      // start measuring immediately and re-sort as results come in
      this.measureLatency(node);
      //   this.sortNodes();
    },
    normalizeUrl(value) {
      if (!value) return "";
      let url = (value || "").trim();
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }
      try {
        new URL(url);
        return url;
      } catch (e) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.legend {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  color: #999;
  font-size: 12px;
}
.legend .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}
.legend .fast {
  background-color: #07c160;
}
.legend .medium {
  background-color: #f4a000;
}
.legend .slow {
  background-color: #ee0a24;
}
.node-port {
  color: #999;
  font-size: 12px;
}
.latency {
  min-width: 64px;
  text-align: right;
  margin-right: 8px;
  font-size: 12px;
}
.latency.fast {
  color: #07c160;
}
.latency.medium {
  color: #f4a000;
}
.latency.slow {
  color: #ee0a24;
}
.footer {
  padding: 12px;
}
.toast-nowrap {
  white-space: nowrap;
}
/* Make popup field label take only needed width and input use remaining space */
/* deep selector for scoped styles in Vue 2 */
.dialog-body /deep/ .van-field__label {
  width: auto !important;
  flex: 0 0 auto;
  margin-right: 8px;
}
.dialog-body /deep/ .van-cell__value {
  flex: 1 1 auto;
}
</style>



// WEBPACK FOOTER //
// src/components/ApiLineSelector.vue